import {
  AssetOhouseSymbolSvg,
  AssetOhouseWordmarkSvg,
} from '@bucketplace/assets';
import { useResponsive } from '@bucketplace/design-system';
import styled from '@emotion/styled';
import gsap from 'gsap';
import {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import type {
  AnimationComponentRef,
  TimelineAnimationStep,
} from '@/brand/hooks';

import {
  BRAND_NAME_SIZE,
  DOCUMENTARY_SIZE,
  MINI_DOCUMENTARY_TEXT1,
  MINI_DOCUMENTARY_TEXT2,
} from '../constants';
import { Container } from '../RebrandSectionContainer';
import { getBreakPoint, getRebrandStyle } from '../utils';
import {
  DocumentaryBrandFilmButton,
  DocumentaryBrandFilmContainer,
  DocumentaryBrandFilmText,
  DocumentaryRollingText,
  DocumentaryText,
  DocumentaryTextContainer,
  DocumentaryVideo,
  VideoContainer,
  VideoSectionWrapper,
} from './Documentary';

const DURATION = {
  NONE: 0,
  SHORT: 0.4,
  MEDIUM: 0.9,
  LONG: 1.4,
};

export const HeroSection = forwardRef<AnimationComponentRef>((_, ref) => {
  const [scrollProgress, setScrollProgress] = useState(0);

  const containerRef = useRef<HTMLDivElement>(null);
  const scrollTriggerRef = useRef<HTMLDivElement>(null);
  const phase1TimelineRef = useRef<gsap.core.Timeline | null>(null);
  const phase2TimelineRef = useRef<gsap.core.Timeline | null>(null);

  /** timeline 최상단 */
  const symbolRef = useRef<HTMLDivElement>(null);
  const wordmarkRef = useRef<HTMLDivElement>(null);
  /** 미니 다큐 배경 */
  const blackOverlayRef = useRef<HTMLDivElement>(null);
  /** 미니 다큐 콘텐츠 */
  const documentaryVideoContainerRef = useRef<HTMLDivElement>(null);
  // const videoContainerRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const documentaryVideo1Ref = useRef<HTMLVideoElement>(null);
  const documentaryVideo2Ref = useRef<HTMLVideoElement>(null);
  const documentaryTextRef = useRef<HTMLParagraphElement>(null);
  /** 미니 다큐 텍스트2 - 수천만의 이야기로 format */
  const rollingTextContainerRef = useRef<HTMLDivElement>(null);
  const rollingTextInnerRef = useRef<HTMLSpanElement>(null);
  /** 다큐 마무리 텍스트 */
  const documentaryBrandFilmTextRef = useRef<HTMLDivElement>(null);
  const documentaryBrandFilmButtonRef = useRef<HTMLButtonElement>(null);

  const { breakpoint } = useResponsive({
    fallbackBreakpoint: 'mobile',
    populateOnInitialRender: true,
    preventSyncOnInitialRender: false,
  });
  const isPc = breakpoint === 'laptop' || breakpoint === 'desktop';

  // Phase 1: Symbol + Wordmark Timeline 설정
  const phase1Config = useMemo(() => {
    return {
      steps: [
        {
          target: containerRef,
          from: { opacity: 0 },
          to: { opacity: 1, duration: DURATION.NONE },
          position: '<',
        },
        {
          target: symbolRef,
          from: {
            xPercent: -50,
            yPercent: isPc ? -50 : -45,
            scale: () => {
              const container = containerRef.current;
              if (!container) {
                return 1;
              }

              const rect = container.getBoundingClientRect();
              const width = Math.round(rect.width);
              const { BigSymbol, symbol } =
                BRAND_NAME_SIZE[getBreakPoint(width)];

              return Math.max(1, BigSymbol / symbol);
            },
          },
          to: {
            xPercent: -176,
            yPercent: -50,
            scale: 1,
            duration: DURATION.MEDIUM,
          },
          position: '>',
        },
        {
          target: wordmarkRef,
          from: { x: isPc ? 26 : 0, y: 2, opacity: 0 },
          to: { opacity: 1, x: 0, xPercent: 26, duration: DURATION.SHORT },
          position: '<+=0.2', // 이전 애니메이션 시작 후 0.5초에 시작
        },
      ],
    };
  }, [isPc]);

  // Phase 2: BlackOverlay + Documentary Timeline 설정
  const phase2Config = useMemo<{ steps: TimelineAnimationStep[] }>(() => {
    return {
      steps: [
        {
          target: blackOverlayRef,
          from: { opacity: 0 },
          to: { opacity: 1, duration: DURATION.MEDIUM },
          position: '<',
        },
        {
          target: documentaryVideoContainerRef,
          from: { opacity: 0 },
          to: { opacity: 1, duration: DURATION.NONE },
          position: '>',
        },
        {
          target: documentaryVideo1Ref,
          from: { y: 50, opacity: 0 },
          to: { y: 0, opacity: 1, duration: DURATION.MEDIUM },
          position: '+=0.3',
        },
        // 텍스트1 노출
        {
          target: documentaryTextRef,
          from: { opacity: 0, y: 10 },
          to: {
            opacity: 1,
            y: 0,
            duration: DURATION.LONG,
          },
          position: '<+=0.3', // 이전 애니메이션 시작 후 0.5초
        },
        // 텍스트1 제거
        {
          target: documentaryTextRef,
          to: {
            opacity: 0,
            y: -10,
            duration: DURATION.LONG,
          },
          position: '>',
        },
        {
          target: documentaryTextRef,
          to: {
            duration: 0.5,
          },
          position: '>',
        },
        // 동영상 스크롤
        // TODO: 볼록 애니메이션 추가
        {
          target: documentaryVideo2Ref,
          from: { yPercent: 100, opacity: 1 },
          to: { yPercent: 0, opacity: 1, duration: DURATION.LONG },
          position: '>',
        },
        {
          target: documentaryVideo1Ref,
          from: { opacity: 1 },
          to: { yPercent: -100, opacity: 1, duration: DURATION.LONG },
          position: '<',
        },
        // 롤링 텍스트 컨테이너 노출 (before/after 텍스트)
        {
          target: rollingTextContainerRef,
          from: { opacity: 0, y: 20 },
          to: {
            opacity: 1,
            y: 0,
            duration: 0.8,
          },
          position: '>-=0.5',
        },
        // 롤링 텍스트 내부 애니메이션
        {
          target: rollingTextInnerRef,
          from: {},
          to: {
            duration: 3,
            onUpdate(this: gsap.core.Timeline) {
              // eslint-disable-next-line react/no-this-in-sfc
              const progress = this.progress();
              setScrollProgress(progress);
            },
          },
          position: '>-=0.1',
        },
        // 동영상1 제거
        {
          target: documentaryVideo1Ref,
          to: {
            opacity: 0,
            duration: 0,
          },
          position: '>+=0.3',
        },
        // 동영상2 border 재설정
        {
          target: documentaryVideo2Ref,
          to: { borderRadius: '16px', duration: 0.01 },
          position: '<',
        },
        // frame 제거
        {
          target: frameRef,
          from: { overflow: 'hidden' },
          to: {
            overflow: 'visible',
            duration: 0,
          },
          position: '<',
        },
        // 다음 텍스트 노출
        // 동영상 확대
        {
          target: documentaryVideo2Ref,
          to: {
            // 실행 시점에 동적 계산
            scale: () => {
              const container = documentaryVideoContainerRef.current;
              if (!container) {
                return 1;
              }

              const rect = container.getBoundingClientRect();
              const width = Math.round(rect.width);
              const size = DOCUMENTARY_SIZE[getBreakPoint(width)];

              return Math.max(1, width / size.width, rect.height / size.height);
            },
            duration: DURATION.LONG,
            ease: 'power2.out',
          },
          position: '>', // 이전 스텝 완료 후
        },
        // 롤링 텍스트 제거
        {
          target: rollingTextContainerRef,
          from: { opacity: 1 },
          to: { y: -10, opacity: 0 },
          position: '<',
        },
        {
          target: documentaryBrandFilmTextRef,
          from: { y: 50, opacity: 0 },
          to: { y: 0, opacity: 1, duration: DURATION.MEDIUM },
          position: '>',
        },
        {
          target: documentaryBrandFilmButtonRef,
          from: { y: 50, opacity: 0 },
          to: {
            y: 0,
            opacity: 1,
            duration: DURATION.SHORT,
            pointerEvents: 'auto',
          },
          position: '-=0.5',
        },
        {
          target: scrollTriggerRef,
          to: {
            duration: 2,
          },
          position: '>',
        },
      ],
    };
  }, []);

  useImperativeHandle(ref, () => ({
    createTimeline() {
      const masterTl = gsap.timeline({ defaults: { ease: 'none' } });

      // Timeline 생성 helper
      const createPhaseTimeline = (config: {
        steps: TimelineAnimationStep[];
      }): gsap.core.Timeline => {
        const tl = gsap.timeline({ defaults: { ease: 'none' } });

        const addStep = (step: TimelineAnimationStep): void => {
          const t = step.target?.current ?? step.target;
          const pos = step.position ?? '>';
          if (!t) {
            return;
          }

          if (step.from && step.to) {
            tl.fromTo(t, step.from, { ...step.to }, pos);
          } else if (step.to) {
            tl.to(t, { ...step.to }, pos);
          } else if (step.from) {
            tl.from(t, { ...step.from }, pos);
          }
        };

        config.steps.forEach(addStep);
        return tl;
      };

      // Phase 1 Timeline 생성 (Symbol + Wordmark)
      const phase1Timeline = createPhaseTimeline(phase1Config);
      phase1TimelineRef.current = phase1Timeline;

      // Phase 2 Timeline 생성 (BlackOverlay + Documentary)
      const phase2Timeline = createPhaseTimeline(phase2Config);
      phase2TimelineRef.current = phase2Timeline;

      // Master Timeline에 Phase별 추가
      masterTl.add(phase1Timeline, 0);

      // Phase 1 완료 후 일정 시간 대기 (스크롤 pause 구간)
      masterTl.to({}, { duration: 1.5 }, '>'); // 1.5초 pause 구간

      // Phase 2 Timeline 추가
      masterTl.add(phase2Timeline, '>');

      return masterTl;
    },
  }));

  return (
    <Container ref={containerRef} position="absolute" top={0} left={0}>
      <div
        ref={scrollTriggerRef}
        style={{
          width: '100%',
          height: '100vh',
          position: 'absolute',
          top: 0,
          left: 0,
        }}
      />
      <LogoContainer>
        <LogoWrapper ref={wordmarkRef}>
          <AssetOhouseWordmarkSvg
            aspect="width"
            width={getRebrandStyle('brandName', 'wordmark', breakpoint)}
          />
        </LogoWrapper>
        <SymbolWrapper ref={symbolRef}>
          <AssetOhouseSymbolSvg
            aspect="width"
            width={getRebrandStyle('brandName', 'symbol', breakpoint)}
          />
        </SymbolWrapper>
      </LogoContainer>
      {/* black 배경 */}
      <BlackOverlay ref={blackOverlayRef} />
      {/* 미니 다큐 동영상 */}
      <VideoSectionWrapper ref={documentaryVideoContainerRef}>
        <VideoContainer ref={frameRef}>
          <DocumentaryVideo
            ref={documentaryVideo1Ref}
            src="https://prs.ohousecdn.com/apne2/events/rebranding/v1-424875541627008.mp4"
          />
          <DocumentaryVideo
            ref={documentaryVideo2Ref}
            src="https://prs.ohousecdn.com/apne2/events/rebranding/v1-424875541446720.mp4"
          />
        </VideoContainer>
      </VideoSectionWrapper>
      {/* 미니 다큐 텍스트 */}
      <DocumentaryTextContainer>
        <DocumentaryText ref={documentaryTextRef}>
          {breakpoint ? MINI_DOCUMENTARY_TEXT1[breakpoint] : null}
        </DocumentaryText>
        <DocumentaryRollingText
          ref={rollingTextContainerRef}
          innerRef={rollingTextInnerRef}
          scrollProgress={scrollProgress}
          before="수많은 "
          after={breakpoint ? MINI_DOCUMENTARY_TEXT2[breakpoint] : undefined}
          rollingText={['이야기', '브랜드', '시공 업체']}
        />
        {/* 다큐 마무리 텍스트 */}
        <DocumentaryBrandFilmContainer>
          <DocumentaryBrandFilmText ref={documentaryBrandFilmTextRef}>
            모두, 오늘의집에 산다
          </DocumentaryBrandFilmText>
          <DocumentaryBrandFilmButton ref={documentaryBrandFilmButtonRef}>
            브랜드 필름 보기
          </DocumentaryBrandFilmButton>
        </DocumentaryBrandFilmContainer>
      </DocumentaryTextContainer>
    </Container>
  );
});

const LogoContainer = styled.div`
  position: relative;
`;

const SymbolWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
`;

const LogoWrapper = styled.div`
  opacity: 0;
`;

const BlackOverlay = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  opacity: 0;
  // pointer-events: none; /** 인터렉션 차단 방지 */
  background-color: #000;
`;
